import { dateRegex } from '@frontend/mwm-ui'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { CellEditingStoppedEvent, GetRowIdParams, SortChangedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { AxiosError } from 'axios'
import type { ChangeEvent, ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { IAppManagementByDeviceType } from '~/types/application-management'
import type { IReplyToReviewsResponse, IReportReviewsResponse, IReview } from '~/types/review'

import columns, { COLUMNS } from './columns'
import ReviewsMonitoringFilters, { defaultState as filtersDefaultState } from './Filters'

import HistoryModal from '../Modals/HistoryModal'
import ReplyModal from '../Modals/ReplyModal'
import ReportModal, { getReportReviewErrorMessage } from '../Modals/ReportModal'

import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import Section from '~/components/Section'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import { AUTHORIZATIONS, DEFAULT_DATE_RANGE, DEVICE_TYPE } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import { exportParams } from '~/const/reviews'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { DEFAULT_SEARCH, DEFAULT_SORT, useTablePagination } from '~/hooks/useTablePagination'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useAppsManagementListQuery } from '~/queries/application-management'
import {
  useReviewsQuery,
  useUpdateReviewTopicsMutation,
  keys as reviewsKeys,
  useReportReviewsMutation,
  useDoNotReportReviewMutation,
} from '~/queries/reviews'
import { useReviewsTopicsQuery } from '~/queries/reviews-topics'
import { REVIEW_STATUS } from '~/types/review'
import { appKeyDeviceTypeToString, splitByDeviceType } from '~/utils/application'
import { debounce } from '~/utils/function'
import { resize } from '~/utils/media'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const ENTRIES_NAME = 'reviews'

const compareArrays = (a: IReview['topics'], b: IOption[]) =>
  a.length === b.length && a.every((element, index) => element.id === b[index].value)

const ReviewsMonitoring = (): ReactElement => {
  /** Local state */

  const { dispatchToSearchParams, searchParamsObject } = useSearchParamsContext()

  const getInitialDate = (type: 'end' | 'start'): string => {
    const dateFromParams = searchParamsObject[type]
    return dateFromParams?.match(dateRegex) ? dateFromParams : DEFAULT_DATE_RANGE[type]
  }

  const initialFilterState = {
    appVersions: searchParamsObject['appVersions[]'] || [],
    countries: searchParamsObject['countries[]'] || [],
    end: getInitialDate('end'),
    languages: searchParamsObject['languages[]'] || [],
    mlLabels: searchParamsObject['mlLabels[]'] || [],
    ratings: searchParamsObject['ratings[]'] || [],
    start: getInitialDate('start'),
    statusFilterType: 'overlap',
    statuses: searchParamsObject['statuses[]'] || [],
    topics: searchParamsObject['topics[]'] || [],
  }

  const appsSearchParam = searchParamsObject['apps[]']

  const reviewsWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.REVIEWS.WRITE])

  const queryClient = useQueryClient()

  const paginationApi = useTablePagination(ENTRIES_NAME, initialFilterState)

  const { filters, orderBy, page, search, setFilters, setOrderBy, setPage, setSearch, size } = paginationApi

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [selectedApps, setSelectedApps] = useState<IAppManagementByDeviceType[]>([])

  const [isHistoryModalOpen, setHistoryModalState] = useState(false)

  const [isReplyModalOpen, setReplyModalState] = useState(false)

  const [isReportModalOpen, setReportModalState] = useState(false)

  const [isDoNotReportDialogOpen, setDoNotReportDialogState] = useState(false)

  const [isReportDialogOpen, setReportDialogState] = useState(false)

  const [selectedReview, setSelectedReview] = useState<IReview>()

  const [selectedReviews, setSelectedReviews] = useState<IReview[]>([])

  const [isActionMulti, setIsActionMulti] = useState<boolean>()

  /** Queries & Mutations */

  const { mutateAsync: report } = useReportReviewsMutation()

  const { mutateAsync: doNotReport } = useDoNotReportReviewMutation()

  const { data: apps } = useAppsManagementListQuery()

  const appsByDeviceType = useMemo(() => apps?.reduce(splitByDeviceType(), [] as IAppManagementByDeviceType[]), [apps])

  const { data: topics } = useReviewsTopicsQuery()

  const params = {
    applications: selectedApps.map(({ app_key, device_type }) => appKeyDeviceTypeToString(app_key, device_type)),
    ['order-by']: orderBy,
    page,
    size,
    ...search,
    ...filters,
    topics: topics?.filter(({ label }) => (filters.topics as string[])?.includes(label)).map(({ id }) => id),
  }

  const { data: reviewsData, isLoading: isReviewsLoading } = useReviewsQuery(params)

  const { mutateAsync: updateReviewTopics } = useUpdateReviewTopicsMutation()

  const rowData = useMemo(
    () =>
      reviewsData?.data?.map((review) => {
        const app = appsByDeviceType?.find(
          ({ app_key, device_type }) => app_key === review.app_key && device_type === review.device_type
        )

        return {
          ...review,
          app_icon_url: app?.icon_file_id ? resize(`${FASTLY_PATH}${app.icon_file_id}`, 32, 32) : undefined,
          app_name: app?.default_display_name || review.app_key,
        }
      }),
    [appsByDeviceType, reviewsData?.data]
  )

  const isOneDeviceTypeOnly = {
    android: selectedApps.every(({ device_type }) => device_type === DEVICE_TYPE.ANDROID),
    ios: selectedApps.every(({ device_type }) => device_type === DEVICE_TYPE.IOS),
  }

  /** Effects */

  useDocumentTitle('Reviews monitoring')

  useEffect(() => {
    if (appsByDeviceType && appsSearchParam?.length) {
      const _selectedApplications =
        appsSearchParam.length === 1
          ? appsByDeviceType.filter(({ app_key, device_type }) =>
              appsSearchParam[0].includes(appKeyDeviceTypeToString(app_key, device_type))
            )
          : appsByDeviceType.filter(({ app_key, device_type }) =>
              appsSearchParam.includes(appKeyDeviceTypeToString(app_key, device_type))
            )

      setSelectedApps(_selectedApplications)
      setPage(0)
    }
  }, [appsByDeviceType, appsSearchParam, setPage])

  useEffect(() => {
    if (appsByDeviceType && !appsSearchParam?.length) {
      setSelectedApps(appsByDeviceType)
      setPage(0)
    }
  }, [appsByDeviceType, appsSearchParam, setPage])

  useEffect(() => {
    const countries = (filters.countries as string[]) || []
    const language = (filters.language as string[]) || []
    const appVersions = (filters.appVersions as string[]) || []
    const ratings = (filters.ratings as string[]) || []

    if (gridRef.current?.api) {
      gridRef.current.api.setColumnsVisible([COLUMNS.APPLICATION], selectedApps.length !== 1)
      gridRef.current.api.setColumnsVisible([COLUMNS.APP_VERSION], appVersions.length !== 1)
      gridRef.current.api.setColumnsVisible([COLUMNS.RATING], ratings.length !== 1)
      gridRef.current.api.setColumnsVisible(
        [COLUMNS.COUNTRY_CODE_LANGUAGE],
        (countries.length !== 1 && language.length === 0) || (countries.length === 0 && language.length !== 1)
      )
    }
  }, [
    filters.appVersions,
    filters.countries,
    selectedApps.length,
    filters.ratings,
    filters.countryCodes,
    filters.language,
  ])

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setColumnsVisible(
        [COLUMNS.TITLE],
        selectedApps.some(({ device_type }) => device_type === DEVICE_TYPE.IOS)
      )
      gridRef.current.api.setColumnsVisible(
        [COLUMNS.DEVICE, COLUMNS.RAM_MB, COLUMNS.SDK_VERSION],
        selectedApps.some(({ device_type }) => device_type === DEVICE_TYPE.ANDROID)
      )
    }
  }, [selectedApps])

  useEffect(() => {
    dispatchToSearchParams({
      type: 'update',
      value: {
        'appVersions[]': filters.appVersions as string[],
        'countries[]': filters.countries as string[],
        end: filters.end as string,
        'languages[]': filters.languages as string[],
        'mlLabels[]': filters.mlLabels as string[],
        'ratings[]': filters.ratings as string[],
        start: filters.start as string,
        'statuses[]': filters.statuses as string[],
        'tags[]': filters.tags as string[],
        'topics[]': filters.topics as string[],
      },
    })
  }, [dispatchToSearchParams, filters])

  /** Handlers */

  const handleSortChange = (event: SortChangedEvent) => {
    const colState = event.api.getColumnState()
    const sortState = colState.filter((s) => s.sort != null)

    setOrderBy(
      sortState.length
        ? `${
            sortState[0].colId.split('_').length - 1 > 1
              ? sortState[0].colId.substring(0, sortState[0].colId.lastIndexOf('_'))
              : sortState[0].colId
          } ${sortState[0].sort}`
        : DEFAULT_SORT
    )
  }

  const handleSearchChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value
    setSearch(text ? { [`${'content_en'}`]: text } : DEFAULT_SEARCH)
    setPage(0)
  }, 500)

  const handleFiltersChange = useCallback(
    (filter: string, values: string | string[]) => {
      setFilters((filters) => ({
        ...filters,
        [filter]: values,
      }))
      setPage(0)
    },
    [setFilters, setPage]
  )

  const handleResetFilters = useCallback(() => {
    setFilters((filters) => ({ ...filters, ...filtersDefaultState }))
    setPage(0)
  }, [setFilters, setPage])

  const handleCellEditionStopped = async (event: CellEditingStoppedEvent<IReview, IReview['topics']>) => {
    // @ts-expect-error newValue is IOption[]
    if (event.data && !compareArrays(event.oldValue || [], (event.newValue || []) as IOption[])) {
      const newReview: IReview = {
        ...event.data,
        // @ts-expect-error newValue is IOption[]
        topics: (event.newValue as IOption[])?.map(({ value }: IOption) => value) || [],
      }

      try {
        await updateReviewTopics(newReview)
      } catch (e) {
        captureException(e)
      }
    }
  }

  const handleGetRowId = useMemo(
    () =>
      ({ data }: GetRowIdParams<IReview>) =>
        data.id,
    []
  )
  const handleHistoryModalState = (isOpen: boolean, review?: IReview) => () => {
    setSelectedReview(review)
    setHistoryModalState(isOpen)
  }

  const handleSelectionChange = () => gridRef.current && setSelectedReviews(gridRef.current.api.getSelectedRows())

  // Export
  const handleExportButtonClick = () => gridRef.current?.api.exportDataAsCsv(exportParams)

  // Reply
  const handleReplyModalState = (isOpen: boolean, isMulti?: boolean, review?: IReview) => () => {
    setIsActionMulti(isMulti)
    setSelectedReview(review)
    setReplyModalState(isOpen)
  }

  const handleReplySuccess = (response: IReplyToReviewsResponse) => {
    setReplyModalState(false)
    queryClient.invalidateQueries({ queryKey: reviewsKeys.list(params) })
    const { errors, nb_reply_error, nb_reply_success, total } = response

    if (!errors) {
      displayToast(`${total > 1 ? `${total} replies` : '1 reply'} successfully created`, 'success')
    } else {
      if (nb_reply_error === total) {
        displayToast(total > 1 ? `${total} replies could not be created` : 'Reply could not be created', 'error')
      } else {
        displayToast(
          `${nb_reply_success} repl${nb_reply_success > 1 ? 'ies' : 'y'} created; ${nb_reply_error} met an error`,
          'error'
        )
      }
    }
  }

  const handleReplyFailure = () => {
    setReplyModalState(false)
    displayToast('Reply (or replies) could not be created', 'error')
  }

  // Report
  const handleReportModalState = (isOpen: boolean) => () => setReportModalState(isOpen)

  const toggleReportDialogState = (isOpen: boolean) => () => setReportDialogState(isOpen)

  const handleSingleReport = (review: IReview) => () => {
    setIsActionMulti(false)
    setSelectedReview(review)
    review.device_type === DEVICE_TYPE.ANDROID ? setReportDialogState(true) : setReportModalState(true)
  }

  const handleMultiReport = () => {
    setIsActionMulti(true)

    const reviewsPendingReportOrReported = selectedReviews?.filter(({ status }) =>
      status.some((el) => [REVIEW_STATUS.PENDING_REPORT, REVIEW_STATUS.REPORTED].includes(el))
    )

    !!reviewsPendingReportOrReported?.length &&
      displayToast(
        reviewsPendingReportOrReported.length === selectedReviews?.length && selectedReviews.length === 1
          ? 'The selected review is already pending report or reported'
          : `${reviewsPendingReportOrReported.length} out of the ${selectedReviews?.length} reviews selected ${
              reviewsPendingReportOrReported.length > 1 ? 'are' : 'is'
            } already pending report or reported`,
        'info'
      )

    selectedReviews?.every(({ device_type }) => device_type === DEVICE_TYPE.ANDROID)
      ? setReportDialogState(true)
      : setReportModalState(true)
  }

  const confirmReport = async () => {
    if (!(selectedReview || selectedReviews)) {
      displayToast('Something went wrong; contact the Frontend team!', 'error')
      return
    }

    try {
      let response
      if (!isActionMulti && selectedReview) {
        response = await report({ review_ids: [selectedReview.id] })
      } else if (isActionMulti && selectedReviews) {
        response = await report({ review_ids: selectedReviews.map(({ id }) => id) })
      }
      await queryClient.invalidateQueries({ queryKey: reviewsKeys.list(params) })
      response && handleReportSuccess(response)
    } catch (e) {
      handleReportFailure(getReportReviewErrorMessage(e as AxiosError<IApiError>))
    }
  }

  const handleReportSuccess = (response: IReportReviewsResponse) => {
    setReportModalState(false)
    setReportDialogState(false)
    const { errors, nb_reply_error, nb_reply_success, total } = response

    if (!errors) {
      displayToast(
        `${total > 1 ? `All ${total} reviews have` : 'The selected review has'} been successfully reported`,
        'success'
      )
    } else {
      if (nb_reply_error === total) {
        displayToast(
          (total > 1 ? `All ${total} reviews have` : 'The selected review has') +
            (errors.every(({ error }) => error.kind === 'already_reported')
              ? ' already been reported'
              : ' met an error'),
          'error'
        )
      } else {
        displayToast(
          `${nb_reply_success} review(s) successfully reported; ${nb_reply_error} review(s) ${
            errors.every(({ error }) => error.kind === 'already_reported') ? 'already been reported' : 'met an error'
          }`,
          'error'
        )
      }
    }
  }

  const handleReportFailure = (errorMessage: string) => {
    setReportModalState(false)
    displayToast(errorMessage, 'error')
  }

  // Do not report
  const toggleDoNotReportDialogState = (isOpen: boolean) => () => setDoNotReportDialogState(isOpen)

  const handleDoNotReport = (review: IReview) => () => {
    setSelectedReview(review)
    setDoNotReportDialogState(true)
  }

  const confirmDoNotReport = async () => {
    try {
      selectedReview &&
        (await doNotReport({
          reviewId: selectedReview.id,
        }))
      await queryClient.invalidateQueries({ queryKey: reviewsKeys.all })
      handleDoNotReportSuccess()
    } catch (e) {
      handleDoNotReportFailure(getReportReviewErrorMessage(e as AxiosError<IApiError>))
    }
  }

  const handleDoNotReportSuccess = () => {
    setReportModalState(false)
    setDoNotReportDialogState(false)
    displayToast('Review not reported!', 'success')
  }

  const handleDoNotReportFailure = (errorMessage: string) => {
    setReportModalState(false)
    displayToast(errorMessage, 'error')
  }

  /** Render */

  return (
    <>
      <Header apps={selectedApps} displaySwitchButton={false} isMulti isSwitchDisabled />
      <ReviewsMonitoringFilters
        initialState={initialFilterState}
        isOneDeviceTypeOnly={isOneDeviceTypeOnly}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        selectedApps={selectedApps}
      />
      <Layout className="reviews-monitoring-view" withPadding>
        <div className="reviews-monitoring-view__content">
          <ContentLayout>
            <Section className="section">
              <ServerSideTableWrapper
                className="reviews-listing-table with-min-height"
                columnDefs={columns}
                context={{
                  handleDoNotReport: handleDoNotReport,
                  handleOpenHistory: handleHistoryModalState,
                  handleReply: handleReplyModalState,
                  handleReport: handleSingleReport,
                  reviewsWriteAuthorized: reviewsWriteAuthorized,
                  topics,
                }}
                currentPage={page}
                editControls={
                  selectedReviews && selectedReviews.length > 0 ? (
                    <div className="multi-actions">
                      {reviewsWriteAuthorized ? (
                        <>
                          <Button className="multi-actions__report" onClick={handleMultiReport}>
                            Report
                          </Button>
                          <Button className="multi-actions__reply" onClick={handleReplyModalState(true, true)}>
                            Reply
                          </Button>
                        </>
                      ) : null}
                      <Button className="multi-actions__export" onClick={handleExportButtonClick}>
                        Export as CSV
                      </Button>
                    </div>
                  ) : null
                }
                entriesName={ENTRIES_NAME}
                getRowId={handleGetRowId}
                loading={isReviewsLoading}
                onCellEditingStopped={handleCellEditionStopped}
                onSearchChange={handleSearchChange}
                onSelectionChanged={handleSelectionChange}
                onSortChanged={handleSortChange}
                paginationApi={paginationApi}
                ref={gridRef}
                rowData={rowData || []}
                rowSelection={{
                  enableClickSelection: true,
                  headerCheckbox: true,
                  mode: 'multiRow',
                }}
                rowStyle={{ cursor: 'default' }}
                suppressScrollOnNewData
                tableRef={tableRef}
                total={reviewsData?.total || 0}
                withSearchBar
              />
            </Section>
          </ContentLayout>
        </div>
      </Layout>
      <ReplyModal
        isOpen={isReplyModalOpen}
        onClose={handleReplyModalState(false)}
        onFailure={handleReplyFailure}
        onSuccess={handleReplySuccess}
        reviews={isActionMulti ? selectedReviews : selectedReview ? [selectedReview] : []}
      />
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={handleReportModalState(false)}
        onFailure={handleReportFailure}
        onSuccess={handleReportSuccess}
        reviews={isActionMulti ? selectedReviews : selectedReview ? [selectedReview] : []}
      />
      <HistoryModal isOpen={isHistoryModalOpen} onClose={handleHistoryModalState(false)} review={selectedReview} />
      <ModalDialog
        btnConfirm={<Button onClick={confirmDoNotReport}>Continue</Button>}
        isOpen={isDoNotReportDialogOpen}
        mainText="Do you really want to NOT report?"
        onClose={toggleDoNotReportDialogState(false)}
        onConfirm={confirmDoNotReport}
      />
      <ModalDialog
        btnConfirm={<Button onClick={confirmReport}>Continue</Button>}
        isOpen={isReportDialogOpen}
        mainText={
          isActionMulti && selectedReviews && selectedReviews.length > 1
            ? `Do you really want to report the ${selectedReviews.length} selected reviews?`
            : 'Do you really want to report the selected review?'
        }
        onClose={toggleReportDialogState(false)}
        onConfirm={confirmReport}
      />
    </>
  )
}

export default ReviewsMonitoring
